import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs';
import { ClienteUnicoServiceProxy, PetDto } from '../../services/service-proxy/service-proxies';
import { VetExtendedServiceProxy } from '../../services/service-proxy/service-proxies-vet';
import { AppService } from '../../services/app.service';
import { PetClinicalService, HistoriaClinica } from '../../services/pet-clinical.service';
import { MatSnackBar } from '@angular/material/snack-bar';

/** Una fila de "Sus datos": el dato, si esta, y por que conviene tenerlo. */
interface FilaDato {
  clave  : string;
  valor  : string | null;
  motivo : string;
}

@Component({
  selector: 'app-pet-detail',
  templateUrl: './pet-detail.component.html',
  styleUrls: ['./pet-detail.component.scss']
})
export class PetDetailComponent {

  isLoading = false;
  idUnico? : string;
  petId? : string;

  pets? : PetDto[];
  pet? : PetDto;

  filas : FilaDato[] = [];

  // Historia clinica real, traida de la API de Vet. Empieza en null (todavia
  // no llego) y pasa a un objeto que puede estar vacio (la mascota no tiene
  // historia, que es el caso mas comun).
  historia? : HistoriaClinica | null;
  cargandoHistoria = false;

  // "Ya no está conmigo": confirmacion inline + guardado del deceso.
  confirmandoDeceso = false;
  guardandoDeceso = false;

  constructor(private appService : AppService,
              private route : ActivatedRoute,
              private clienteUnicoService : ClienteUnicoServiceProxy,
              private clinico : PetClinicalService,
              private vetExtendedService : VetExtendedServiceProxy,
              private router : Router,
              private snackBar : MatSnackBar) {

    this.idUnico = appService.idUnico;

    // Cambiar de mascota sin salir de la ficha reusa el componente, asi que
    // hay que rearmar los datos con cada cambio de parametro.
    this.route.paramMap.subscribe(params => {
      this.petId = params.get('pet_id') ?? undefined;
      if (this.pets) this.armar();
    });
  }

  ngOnInit() {
    this.cargar();
  }

  private cargar() {

    const cache = this.appService.getClienteAppData(this.idUnico!);

    if (cache) {
      this.pets = cache.pets;
      this.armar();
      return;
    }

    this.isLoading = true;
    this.clienteUnicoService
        .getForApp(this.idUnico, null)
        .pipe(finalize(() => this.isLoading = false))
        .subscribe(respuesta => {
          this.pets = respuesta.pets;
          this.appService.saveClienteAppData(respuesta);
          // Antes faltaba este armar(): al entrar directo a la ficha sin pasar
          // por la home no habia cache, se pedian los datos y no se hacia nada
          // con ellos, asi que la pantalla quedaba vacia con un refresh o con
          // un link pegado.
          this.armar();
        });
  }

  private armar() {

    this.pet = this.pets?.find(p => p.id === Number.parseInt(this.petId!));

    if (!this.pet) {
      this.filas = [];
      this.historia = null;
      return;
    }

    this.cargarHistoria(this.pet.id);

    // Solo campos que existen en PetDto Y son editables en el formulario. El
    // prototipo muestra ademas Peso y Castrado, que no estan en ninguno de los
    // dos: el peso se muestra abajo, en el bloque de Etapa 2, marcado como
    // dato de ejemplo; castrado no tiene donde ir.
    this.filas = [
      { clave: 'Especie',    valor: this.especie,
        motivo: 'Para recomendarte lo que le corresponde.' },
      { clave: 'Raza',       valor: this.pet.breed?.name ?? null,
        motivo: 'Para los cuidados y el alimento propios de su raza.' },
      { clave: 'Sexo',       valor: this.pet.gender?.name ?? null,
        motivo: 'Para los controles que le corresponden.' },
      { clave: 'Nacimiento', valor: this.nacimiento,
        motivo: 'Para saber en qué etapa de su vida está y avisarte de los controles que le tocan.' },
      { clave: 'Microchip',  valor: this.pet.microChip ?? null,
        motivo: 'Para poder identificarlo si alguna vez se pierde.' }
    ];
  }

  /** CANINO/FELINO son los valores que devuelve la API; en pantalla no van asi. */
  private get especie() : string | null {

    const nombre = this.pet?.species?.name;

    if (!nombre) return null;
    if (nombre === 'CANINO') return 'Perro';
    if (nombre === 'FELINO') return 'Gato';

    return nombre;
  }

  /** "14 de mayo de 2022, 4 años", como el prototipo. */
  private get nacimiento() : string | null {

    const fecha = this.pet?.birthdate;
    if (!fecha) return null;

    const texto = fecha.locale('es').format('D [de] MMMM [de] YYYY');
    const edad = this.appService.calcularEdad(fecha.format('YYYY-MM-DD'));

    // Una fecha disparatada da una edad de tres digitos: mejor la fecha sola
    // que "137 años".
    if (!edad || edad['años'] >= 100 || edad['años'] < 0) return texto;

    if (edad['años'] < 1) {
      const meses = edad['meses'];
      return meses > 0 ? `${texto}, ${meses} ${meses === 1 ? 'mes' : 'meses'}` : texto;
    }

    return `${texto}, ${edad['años']} ${edad['años'] === 1 ? 'año' : 'años'}`;
  }

  get cargados() : FilaDato[] {
    return this.filas.filter(f => f.valor);
  }

  /**
   * Lo que falta se pide en su propio bloque, con el motivo al lado, y no como
   * un "Falta" repetido en la lista de arriba. Es lo que pide la nota del
   * prototipo: "Tres filas seguidas diciendo Falta se leen como un reproche".
   */
  get faltantes() : FilaDato[] {
    return this.filas.filter(f => !f.valor);
  }

  get perfilCompleto() : boolean {
    return this.filas.length > 0 && this.faltantes.length === 0;
  }

  /** Porcentaje para la barra del medidor. */
  get progreso() : number {
    if (!this.filas.length) return 0;
    return Math.round((this.cargados.length / this.filas.length) * 100);
  }

  private cargarHistoria(petId : number) {

    this.historia = null;
    this.cargandoHistoria = true;

    this.clinico
        .obtenerHistoria(petId)
        .pipe(finalize(() => this.cargandoHistoria = false))
        .subscribe(h => this.historia = h);
  }

  /** Hay al menos una visita o vacuna registrada por la veterinaria. */
  get tieneHistoria() : boolean {
    return !!this.historia && this.historia.visitas.length > 0;
  }

  get tieneRecordatorios() : boolean {
    return !!this.historia && this.historia.recordatorios.length > 0;
  }

  /** Se dibuja la curva solo con dos o mas mediciones: un punto no es curva. */
  get tieneCurva() : boolean {
    return (this.historia?.peso.length ?? 0) >= 2;
  }

  get pesoActual() : number | null {
    const serie = this.historia?.peso;
    return serie && serie.length ? serie[serie.length - 1].kg : null;
  }

  /** Puntos de la curva de peso real, ya escalados al viewBox del SVG. */
  get puntosCurva() {

    const serie = this.historia?.peso ?? [];
    if (!serie.length) return [];

    const kilos = serie.map(p => p.kg);
    const min = Math.min(...kilos);
    const max = Math.max(...kilos);
    const rango = max - min || 1;

    return serie.map((p, i) => ({
      kg : p.kg,
      etiqueta : this.mesCorto(p.fecha),
      x : 70 + i * ((600 - 70) / Math.max(serie.length - 1, 1)),
      y : 110 - ((p.kg - min) / rango) * 70,
      ultimo : i === serie.length - 1
    }));
  }

  get lineaCurva() : string {
    return this.puntosCurva.map(p => `${p.x},${p.y}`).join(' ');
  }

  /** "ago 25" a partir de un ISO, para el eje de la curva. */
  private mesCorto(iso : string) : string {
    const meses = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return `${meses[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`;
  }

  getImage() {
    return this.appService.selectImageForPet(this.pet!);
  }

  getImageDefault() {
    return this.appService.selectImageForPet(this.pet!, true);
  }

  /**
   * Mismo patron que order-detail y order-list-item.
   *
   * La version anterior de la ficha tenia (error)="getImageDefault()", que
   * calcula la URL y la descarta sin asignarla: si la foto de S3 no cargaba,
   * quedaba la imagen rota en vez de la generica de la especie.
   */
  loadErrorImage(event : Event) {
    (event.target as HTMLImageElement).src = this.getImageDefault();
  }

  // ── "Ya no está conmigo" (deceso) ──────────────────────────────────────────

  pedirConfirmacionDeceso() { this.confirmandoDeceso = true; }
  cancelarDeceso() { this.confirmandoDeceso = false; }

  /**
   * Marca la mascota como fallecida. Arma un CreateUpdatePetDto con los datos
   * actuales y usa markPetDeceased (que agrega deceased:true, ya que el DTO de
   * update no lo expone). Al terminar invalida la cache y vuelve a Mis mascotas.
   */
  confirmarDeceso() {

    if (!this.pet || this.guardandoDeceso) return;

    // customerId = id del cliente en Vet (la titularidad de la mascota). Es lo que
    // pide Pet/SetDecaesed; sin titularidad no se puede marcar el deceso.
    const customerId = this.pet.owners?.[0]?.id;
    if (!customerId) {
      this.snackBar.open('No pudimos identificar la titularidad de la mascota.', '', { duration: 3500 });
      return;
    }

    this.guardandoDeceso = true;
    this.vetExtendedService
        .markPetDeceased(this.pet.id, customerId)
        .pipe(finalize(() => this.guardandoDeceso = false))
        .subscribe({
          next: () => {
            if (this.pet) this.pet.deceased = true;
            this.confirmandoDeceso = false;
            // La cache tiene la lista con la mascota "activa": hay que refrescarla.
            this.appService.deleteClienteAppData();
            this.snackBar.open('Actualizamos la ficha. Ya no te vamos a escribir sobre ' + (this.pet?.name ?? 'tu mascota') + '.', '', { duration: 3500 });
            this.router.navigateByUrl('/mis-mascotas');
          },
          error: () => {
            this.snackBar.open('No pudimos actualizar la ficha. Probá de nuevo en un rato.', '', { duration: 3500 });
          }
        });
  }
}
